/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';

function Section({ title, children }: { title: string; children: ReactElement | ReactElement[] | string }) {
    return (
        <div className="mb-10">
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl font-light mb-4 text-foreground">
                {title}
            </h2>
            <div className="text-muted-foreground leading-relaxed text-sm space-y-3">{children}</div>
        </div>
    );
}

export default function Returns(): ReactElement {
    return (
        <div className="pb-16">
            <SeoMeta
                title="Returns & Exchanges — Baggallini"
                description="Learn about the Baggallini 30-day return policy, how to initiate a return online, refund timelines, and what items are eligible."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Returns &amp; Exchanges</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="max-w-screen-md mx-auto px-6 pt-10">
                <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light tracking-wide mb-3">
                    Returns &amp; Exchanges
                </h1>
                <p className="text-muted-foreground mb-10 max-w-xl">
                    We want you to love your Baggallini bag. If something isn&apos;t right, here&apos;s how we make it
                    easy.
                </p>

                {/* Quick summary card */}
                <div className="bg-[#F5F3F0] rounded-2xl px-8 py-6 mb-10 grid sm:grid-cols-3 gap-6 text-center">
                    {[
                        { stat: '30 Days', label: 'to return unused items' },
                        { stat: '5–10 Days', label: 'for your refund to appear' },
                        { stat: 'Free', label: 'prepaid return label' },
                    ].map((item) => (
                        <div key={item.stat}>
                            <p
                                style={{ fontFamily: 'var(--font-display)' }}
                                className="text-3xl font-light mb-1 text-foreground">
                                {item.stat}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-[0.08em]">{item.label}</p>
                        </div>
                    ))}
                </div>

                <Section title="Return Policy">
                    <p>
                        Unused merchandise purchased through baggallini.com is eligible for a full refund within{' '}
                        <strong className="text-foreground">30 days of date of purchase</strong>. Items must be in their
                        original, unused condition.
                    </p>
                    <p>
                        Items marked as <strong className="text-foreground">Final Sale</strong> are not eligible for
                        return, refund, or exchange. We reserve the right to approve or refuse returns that do not meet
                        our policy requirements.
                    </p>
                    <p>
                        Purchases made at department stores or specialty retail stores cannot be returned to Baggallini.
                        Please contact the retailer directly for their return policy.
                    </p>
                </Section>

                <Section title="How to Return">
                    <p>
                        Sign in to your Baggallini account or visit our returns page and enter your order number, email
                        address, and billing zip code to start the return process.
                    </p>
                    <p>
                        Once your return is approved, a prepaid return label will be emailed to you. Pack the item(s)
                        securely and attach the label to the outside of the package.
                    </p>
                    <p className="text-xs bg-[#F5F3F0] border border-[#E0DDD8] rounded-lg px-4 py-3 text-foreground/70">
                        <strong>Note:</strong> Original shipping charges are non-refundable.
                    </p>
                </Section>

                <Section title="Exchanges">
                    <p>
                        We do not process direct exchanges. To get a different style or color, simply return the
                        original item for a refund and place a new order for your preferred bag.
                    </p>
                </Section>

                <Section title="Refunds">
                    <p>
                        Refunds are credited to your original payment method. Please allow{' '}
                        <strong className="text-foreground">5–10 business days</strong> for the credit to appear after
                        we receive and process your return.
                    </p>
                </Section>

                <Section title="Warranty">
                    <p>
                        Baggallini stands behind the quality of our bags. If you experience a manufacturing defect,
                        please contact our customer service team at{' '}
                        <a
                            href="mailto:service@baggallini.com"
                            className="text-foreground underline underline-offset-2 hover:no-underline">
                            service@baggallini.com
                        </a>{' '}
                        with your order number and photos showing the defect.
                    </p>
                </Section>

                {/* Help strip */}
                <div className="border-t border-[#e8e8e8] pt-8 mt-4 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-foreground mb-1">Still have questions?</p>
                        <p className="text-sm text-muted-foreground">
                            Our team is available Mon–Fri, 9am–5pm EST at{' '}
                            <a href="tel:18004488753" className="text-foreground hover:underline">
                                1-800-448-8753
                            </a>
                            .
                        </p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                        <Link
                            to="/faq"
                            className="text-sm border border-foreground/30 rounded-full px-4 py-1.5 hover:bg-foreground hover:text-background transition-colors">
                            FAQ
                        </Link>
                        <Link
                            to="/contact-us"
                            className="text-sm bg-foreground text-background rounded-full px-4 py-1.5 hover:opacity-80 transition-opacity">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
